import { useRef, useState } from "react";
import type { Editor } from "@tiptap/react";

import { CeIcon } from "./CeIcon";
import { kopVoetInvoegen, kopVoetPositie, kopVoetVerwijderen, type KopVoetSoort } from "./commEditorExtensions";
import type { useTranslate } from "@refinedev/core";

const KOPVOET_SOORTEN: KopVoetSoort[] = ["kop", "kopEerste", "voet"];

// communicatie (Slice 6 + formatting parity): the formatting toolbar for the template editor. Plain
// <button>/<select> elements (not @fawkes/ui Button — these are dense icon toggles with an active state
// the shared Button doesn't model) composed locally; no shared component is edited. Each control reflects
// the editor's active mark/node so the user sees current state. Mirrors the Jmix editor's toolbar
// (comm-editor.js): bold/italic/underline/strike, headings, lists, blockquote, undo/redo, alignment incl.
// justify, text colour, font family + size, hyperlink, table insert/edit, image-from-computer, rules,
// page-break, signature.

type Translate = ReturnType<typeof useTranslate>;

// Font choices mirror the Jmix dropdown (comm-editor.js ~:1457). Empty value = clear back to default.
const FONT_FAMILIES = ["Inter", "Arial", "Georgia", "Times New Roman"] as const;
// Size choices mirror the Jmix dropdown (~:1459); stored as a px value on the textStyle mark.
const FONT_SIZES = ["10", "11", "12", "14", "16", "18"] as const;

function TbButton({
  active,
  disabled,
  title,
  onClick,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`ce-tb${active ? " on" : ""}`}
      title={title}
      aria-label={title}
      aria-pressed={active ? true : undefined}
      disabled={disabled}
      // Keep editor focus/selection: prevent the mousedown from blurring the ProseMirror selection.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * Een tegel uit de invoegrij: icoon boven het woord. De bron geeft die knoppen bewust meer gewicht
 * dan de opmaakknoppen — je zoekt ze op naam ("Tabel", "Handtekening"), niet op vorm.
 */
function TbTile({
  icoon,
  label,
  titel,
  active,
  disabled,
  onClick,
}: {
  icoon: string;
  label: string;
  /** De uitleg in de tooltip; het label onder het icoon is één woord. */
  titel?: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`ce-tb2${active ? " on" : ""}`}
      title={titel ?? label}
      aria-pressed={active ? true : undefined}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      <CeIcon naam={icoon} />
      <span>{label}</span>
    </button>
  );
}

export function EditorToolbar({
  editor,
  t,
  disabled,
}: {
  editor: Editor;
  t: Translate;
  disabled?: boolean;
}) {
  const c = () => editor.chain().focus();
  const [showLink, setShowLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [showTable, setShowTable] = useState(false);
  const [showKopVoet, setShowKopVoet] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Reflect the current selection's style so the selects show the active value (or the default option).
  const activeFamily = (editor.getAttributes("textStyle").fontFamily as string | undefined) ?? "";
  const activeSize = ((editor.getAttributes("textStyle").fontSize as string | undefined) ?? "").replace(
    /px$/,
    "",
  );
  const activeColor = (editor.getAttributes("textStyle").color as string | undefined) ?? "#000000";

  function openLinkDialog() {
    const prev = (editor.getAttributes("link").href as string | undefined) ?? "";
    setLinkUrl(prev);
    setShowLink(true);
  }

  function applyLink() {
    const url = linkUrl.trim();
    if (url === "") {
      c().extendMarkRange("link").unsetLink().run();
    } else if (editor.state.selection.empty) {
      // No selection: insert the URL text itself as a link (mirrors Jmix behaviour).
      c()
        .insertContent({ type: "text", text: url, marks: [{ type: "link", attrs: { href: url } }] })
        .run();
    } else {
      c().extendMarkRange("link").setLink({ href: url }).run();
    }
    setShowLink(false);
  }

  function removeLink() {
    c().extendMarkRange("link").unsetLink().run();
    setShowLink(false);
  }

  // Insert an image chosen from the computer as a self-contained base64 data: URI — the SAME approach
  // the insert-from-library picker uses, so it renders in editor + preview + PDF. (Jmix comm-editor.js
  // ~:1259 _fileToImage does the identical FileReader→dataURL→setImage.)
  function onPickImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      if (typeof src === "string") {
        editor.chain().focus().insertContent({ type: "image", attrs: { src } }).run();
      }
    };
    reader.readAsDataURL(file);
  }

  const inTable = editor.isActive("table");

  return (
    <div className="ce-toolbar" role="toolbar" aria-label={t("communicatieEditor.toolbar.label")}>
      <div className="ce-trow ce-trow-fmt">
      {/* undo / redo */}
      <TbButton
        title={t("communicatieEditor.toolbar.undo")}
        disabled={disabled || !editor.can().undo()}
        onClick={() => c().undo().run()}
      >
        <CeIcon naam="undo" />
      </TbButton>
      <TbButton
        title={t("communicatieEditor.toolbar.redo")}
        disabled={disabled || !editor.can().redo()}
        onClick={() => c().redo().run()}
      >
        <CeIcon naam="redo" />
      </TbButton>


      <TbButton
        title={t("communicatieEditor.toolbar.bold")}
        active={editor.isActive("bold")}
        disabled={disabled}
        onClick={() => c().toggleBold().run()}
      >
        <b>B</b>
      </TbButton>
      <TbButton
        title={t("communicatieEditor.toolbar.italic")}
        active={editor.isActive("italic")}
        disabled={disabled}
        onClick={() => c().toggleItalic().run()}
      >
        <i>I</i>
      </TbButton>
      <TbButton
        title={t("communicatieEditor.toolbar.underline")}
        active={editor.isActive("underline")}
        disabled={disabled}
        onClick={() => c().toggleUnderline().run()}
      >
        <u>U</u>
      </TbButton>
      <TbButton
        title={t("communicatieEditor.toolbar.strike")}
        active={editor.isActive("strike")}
        disabled={disabled}
        onClick={() => c().toggleStrike().run()}
      >
        <s>S</s>
      </TbButton>


      <TbButton
        title={t("communicatieEditor.toolbar.h1")}
        active={editor.isActive("heading", { level: 1 })}
        disabled={disabled}
        onClick={() => c().toggleHeading({ level: 1 }).run()}
      >
        H1
      </TbButton>
      <TbButton
        title={t("communicatieEditor.toolbar.h2")}
        active={editor.isActive("heading", { level: 2 })}
        disabled={disabled}
        onClick={() => c().toggleHeading({ level: 2 }).run()}
      >
        H2
      </TbButton>
      <TbButton
        title={t("communicatieEditor.toolbar.paragraph")}
        active={editor.isActive("paragraph")}
        disabled={disabled}
        onClick={() => c().setParagraph().run()}
      >
        ¶
      </TbButton>


      {/* font family + size + colour */}
      <select
        className="comm-editor__tb-select"
        title={t("communicatieEditor.toolbar.fontFamily")}
        aria-label={t("communicatieEditor.toolbar.fontFamily")}
        disabled={disabled}
        value={activeFamily}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "") c().unsetFontFamily().run();
          else c().setFontFamily(v).run();
        }}
      >
        <option value="">{t("communicatieEditor.toolbar.fontFamilyDefault")}</option>
        {FONT_FAMILIES.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
      </select>
      <select
        className="comm-editor__tb-select comm-editor__tb-select--narrow"
        title={t("communicatieEditor.toolbar.fontSize")}
        aria-label={t("communicatieEditor.toolbar.fontSize")}
        disabled={disabled}
        value={activeSize}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => {
          const v = e.target.value;
          // setFontSize / unsetFontSize are the custom FontSize extension's commands (see
          // commEditorExtensions.ts); cast keeps us off a global Commands augmentation.
          const cc = c() as unknown as {
            setFontSize: (s: string) => { run: () => boolean };
            unsetFontSize: () => { run: () => boolean };
          };
          if (v === "") cc.unsetFontSize().run();
          else cc.setFontSize(`${v}px`).run();
        }}
      >
        <option value="">{t("communicatieEditor.toolbar.fontSizeDefault")}</option>
        {FONT_SIZES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      <label
        className="comm-editor__tb-color"
        title={t("communicatieEditor.toolbar.color")}
        onMouseDown={(e) => e.preventDefault()}
      >
        <span className="comm-editor__tb-color-swatch" style={{ background: activeColor }} aria-hidden="true">
          A
        </span>
        <input
          type="color"
          aria-label={t("communicatieEditor.toolbar.color")}
          disabled={disabled}
          value={/^#[0-9a-f]{6}$/i.test(activeColor) ? activeColor : "#000000"}
          onChange={(e) => c().setColor(e.target.value).run()}
        />
      </label>
      <TbButton
        title={t("communicatieEditor.toolbar.colorClear")}
        disabled={disabled}
        onClick={() => c().unsetColor().run()}
      >
        A⃠
      </TbButton>


      {/* lists + blockquote */}
      <TbButton
        title={t("communicatieEditor.toolbar.bulletList")}
        active={editor.isActive("bulletList")}
        disabled={disabled}
        onClick={() => c().toggleBulletList().run()}
      >
        • —
      </TbButton>
      <TbButton
        title={t("communicatieEditor.toolbar.orderedList")}
        active={editor.isActive("orderedList")}
        disabled={disabled}
        onClick={() => c().toggleOrderedList().run()}
      >
        1.
      </TbButton>
      <TbButton
        title={t("communicatieEditor.toolbar.blockquote")}
        active={editor.isActive("blockquote")}
        disabled={disabled}
        onClick={() => c().toggleBlockquote().run()}
      >
        ❝
      </TbButton>


      {/* alignment incl. justify */}
      <TbButton
        title={t("communicatieEditor.toolbar.alignLeft")}
        active={editor.isActive({ textAlign: "left" })}
        disabled={disabled}
        onClick={() => c().setTextAlign("left").run()}
      >
        ⯇
      </TbButton>
      <TbButton
        title={t("communicatieEditor.toolbar.alignCenter")}
        active={editor.isActive({ textAlign: "center" })}
        disabled={disabled}
        onClick={() => c().setTextAlign("center").run()}
      >
        ≡
      </TbButton>
      <TbButton
        title={t("communicatieEditor.toolbar.alignRight")}
        active={editor.isActive({ textAlign: "right" })}
        disabled={disabled}
        onClick={() => c().setTextAlign("right").run()}
      >
        ⯈
      </TbButton>
      <TbButton
        title={t("communicatieEditor.toolbar.alignJustify")}
        active={editor.isActive({ textAlign: "justify" })}
        disabled={disabled}
        onClick={() => c().setTextAlign("justify").run()}
      >
        ☰
      </TbButton>

      </div>

      <div className="ce-trow ce-trow-insert">
      {/* hyperlink */}
      <div className="comm-editor__tb-pop-wrap">
        <TbTile
          icoon="link"
          label={t("communicatieEditor.tegel.link")}
        titel={t("communicatieEditor.toolbar.link")}
          active={editor.isActive("link")}
          disabled={disabled}
          onClick={() => (showLink ? setShowLink(false) : openLinkDialog())}
        />
        {showLink && (
          <div className="comm-editor__tb-pop" role="dialog" aria-label={t("communicatieEditor.link.dialogTitle")}>
            <label className="comm-editor__tb-pop-lbl" htmlFor="comm-link-url">
              {t("communicatieEditor.link.urlLabel")}
            </label>
            <input
              id="comm-link-url"
              className="comm-editor__tb-pop-input"
              type="url"
              autoFocus
              placeholder={t("communicatieEditor.link.urlPlaceholder")}
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyLink();
                if (e.key === "Escape") setShowLink(false);
              }}
            />
            <div className="comm-editor__tb-pop-actions">
              <button type="button" className="comm-editor__tb-pop-btn is-primary" onClick={applyLink}>
                {t("communicatieEditor.link.apply")}
              </button>
              {editor.isActive("link") && (
                <button type="button" className="comm-editor__tb-pop-btn" onClick={removeLink}>
                  {t("communicatieEditor.link.remove")}
                </button>
              )}
              <button type="button" className="comm-editor__tb-pop-btn" onClick={() => setShowLink(false)}>
                {t("communicatieEditor.link.cancel")}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* image from computer */}
      <TbTile
        icoon="image"
        label={t("communicatieEditor.tegel.image")}
        titel={t("communicatieEditor.toolbar.image")}
        disabled={disabled}
        onClick={() => imageInputRef.current?.click()}
      />
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={onPickImage}
      />

      {/* table insert + edit */}
      <div className="comm-editor__tb-pop-wrap">
        <TbTile
          label={t("communicatieEditor.tegel.table")}
        titel={t("communicatieEditor.toolbar.table")}
          icoon="table"
          active={inTable}
          disabled={disabled}
          onClick={() => setShowTable((v) => !v)}
        />
        {showTable && (
          <div className="comm-editor__tb-pop comm-editor__tb-pop--table" role="menu">
            <button
              type="button"
              className="comm-editor__tb-pop-btn is-primary"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                c().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
                setShowTable(false);
              }}
            >
              {t("communicatieEditor.table.insert")}
            </button>
            {inTable && (
              <>
                <div className="comm-editor__tb-pop-head">{t("communicatieEditor.table.editHeading")}</div>
                {(
                  [
                    ["addRowBefore", "communicatieEditor.table.addRowBefore"],
                    ["addRowAfter", "communicatieEditor.table.addRowAfter"],
                    ["deleteRow", "communicatieEditor.table.deleteRow"],
                    ["addColumnBefore", "communicatieEditor.table.addColumnBefore"],
                    ["addColumnAfter", "communicatieEditor.table.addColumnAfter"],
                    ["deleteColumn", "communicatieEditor.table.deleteColumn"],
                    ["mergeCells", "communicatieEditor.table.mergeCells"],
                    ["splitCell", "communicatieEditor.table.splitCell"],
                    ["toggleHeaderRow", "communicatieEditor.table.toggleHeaderRow"],
                    ["deleteTable", "communicatieEditor.table.deleteTable"],
                  ] as const
                ).map(([cmd, key]) => (
                  <button
                    key={cmd}
                    type="button"
                    className={`comm-editor__tb-pop-btn${cmd === "deleteTable" || cmd === "deleteRow" || cmd === "deleteColumn" ? " is-danger" : ""}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      // All of these are TableKit commands; call via the typed chain.
                      const chain = c() as unknown as Record<string, () => { run: () => boolean }>;
                      chain[cmd]().run();
                    }}
                  >
                    {t(key)}
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>


      {/* structural inserts */}
      <TbTile
        icoon="rule"
        label={t("communicatieEditor.tegel.hr")}
        titel={t("communicatieEditor.toolbar.hr")}
        disabled={disabled}
        onClick={() => c().setHorizontalRule().run()}
      />
      <TbTile
        icoon="pagebreak"
        label={t("communicatieEditor.tegel.pageBreak")}
        titel={t("communicatieEditor.toolbar.pageBreak")}
        disabled={disabled}
        onClick={() => c().insertContent({ type: "pageBreak" }).run()}
      />
      <TbTile
        icoon="sign"
        label={t("communicatieEditor.tegel.signature")}
        titel={t("communicatieEditor.toolbar.signature")}
        disabled={disabled}
        onClick={() => c().insertContent({ type: "signature" }).run()}
      />

      {/* Kop- en voettekst: in de PDF in de paginamarge, op elke pagina; pagina 1 mag een eigen koptekst. */}
      <div className="comm-editor__tb-pop-wrap">
        <TbTile
          icoon="kopvoet"
          label={t("communicatieEditor.tegel.kopVoet")}
          titel={t("communicatieEditor.toolbar.kopVoet")}
          active={editor.isActive("kopVoet")}
          disabled={disabled}
          onClick={() => setShowKopVoet((v) => !v)}
        />
        {showKopVoet && (
          <div className="comm-editor__tb-pop comm-editor__tb-pop--kopvoet" role="dialog"
            aria-label={t("communicatieEditor.toolbar.kopVoet")}>
            {KOPVOET_SOORTEN.map((soort) => {
              const heeft = kopVoetPositie(editor, soort) !== null;
              return (
                <div key={soort} className="comm-editor__kopvoet-rij">
                  <span className="comm-editor__kopvoet-naam">{t(`communicatieEditor.kopVoet.${soort}`)}</span>
                  <button
                    type="button"
                    className="comm-editor__tb-pop-btn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      kopVoetInvoegen(editor, soort);
                      setShowKopVoet(false);
                    }}
                  >
                    {t(heeft ? "communicatieEditor.kopVoet.edit" : "communicatieEditor.kopVoet.add")}
                  </button>
                  {heeft && (
                    <button
                      type="button"
                      className="comm-editor__tb-pop-btn is-danger"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => kopVoetVerwijderen(editor, soort)}
                    >
                      {t("communicatieEditor.kopVoet.remove")}
                    </button>
                  )}
                </div>
              );
            })}
            <p className="comm-editor__kopvoet-uitleg">{t("communicatieEditor.kopVoet.hint")}</p>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
